import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, BadgeCheck, Bot, Check, ChevronDown, ShieldCheck, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import SampleMonthlyReport from "@/components/SampleMonthlyReport";
import WhatYouGet from "@/components/WhatYouGet";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const STATS = [
  { big: "4+", small: "AI engines tracked — ChatGPT, Gemini, Claude & more" },
  { big: "7", small: "checkpoints run on your account every month" },
  { big: "$597/mo", small: "flat — month-to-month, cancel anytime" },
  { big: "1", small: "plain-English dashboard — you see what moved and why" },
];

const FITS = [
  "Businesses invisible in ChatGPT, Gemini and Claude answers",
  "Companies who want AI engines to name them",
  "Home-service owners who want AI engines to name them",
  "Anyone tired of guessing whether AI marketing is real",
];

const ROUTINE = [
  {
    num: "01",
    title: "Audit & access",
    body: "Facts made identical everywhere, AI crawlers unblocked (llms.txt, robots rules, server-side rendering for bots) and your site foundation checked line by line.",
  },
  {
    num: "02",
    title: "Publish the citable stuff",
    body: "Unique, specific pages AI engines can quote — the answers your buyers actually ask, written in plain sentences.",
  },
  {
    num: "03",
    title: "Build the authority",
    body: "Placement across the web that makes engines trust the answer — reviews, mentions, citations and authority signals.",
  },
  {
    num: "04",
    title: "Track, report, repeat",
    body: "Every month we ask the engines who's the best in your area, log when and how you're named, and show you exactly what moved and why.",
  },
];

const FAQS = [
  {
    q: "What exactly is included in $597/mo?",
    a: "The full AI Visibility Program run by our team: crawler access opened up, citable pages published, authority built across the web, and a monthly report showing the example queries we track and exactly where you stand.",
  },
  {
    q: "How is this different from regular SEO?",
    a: "Regular SEO gets you ranked on Google. This is the new discipline: making AI engines recommend you when people ask who the best option is. It works great alongside our full SEO campaigns — the authority work helps each side.",
  },
  {
    q: "Which AI platforms do you track?",
    a: "ChatGPT, Gemini and Claude — the three your customers are actually asking. Your monthly report shows how often each one names your business for your target searches, month over month.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Month-to-month, no long contract. We keep clients by getting results, not by locking doors.",
  },
];

function AsvFaqRow({ item, open, onToggle, idx }) {
  return (
    <div
      data-testid={`asv-faq-item-${idx}`}
      className="rounded-2xl border border-slate-200 bg-white"
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-semibold text-slate-900">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-flame transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="px-6 pb-6 text-lg leading-relaxed text-mist">{item.a}</p>
      )}
    </div>
  );
}

export default function AiSearchVisibilityPage() {
  const [faq, setFaq] = useState(-1);
  const navigate = useNavigate();
  const goBook = () => {
    navigate("/");
    setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
  };

  return (
    <main>
      <Seo
        title="AI Search Visibility Services (GEO) — ZD Design"
        description="AI Engine optimization: crawler access, citable pages and authority building so ChatGPT, Gemini and Claude name your business. Works great alongside full SEO campaigns. Month-to-month, led by Todd Meisler."
        path="/ai-search-visibility"
      />
      <section className="grain relative overflow-hidden pb-16 pt-40 md:pb-24 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-16 lg:px-12">
          <div>
            <FadeUp>
              <Eyebrow>AI Search Visibility (GEO)</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Get named in{" "}
              <span className="text-flame">AI answers.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Your customers are asking ChatGPT, Gemini and Claude who the
              best option is — and the AI names only a few businesses. Our
              AI Visibility Program is built to make yours one of them:{" "}
              <strong className="font-bold text-slate-900">
                named, quoted and recommended in AI answers.
              </strong>
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-3 md:grid-cols-4">
              {STATS.map((s, i) => (
                <div
                  key={s.big}
                  data-testid={`asv-stat-${i}`}
                  className="glass rounded-2xl border border-slate-200 p-5"
                >
                  <p className="font-display text-2xl font-bold text-slate-900">
                    {s.big}
                  </p>
                  <p className="mt-1 text-base leading-snug text-faint">{s.small}</p>
                </div>
              ))}
            </div>
          </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <div data-testid="asv-hero-photo" className="overflow-hidden rounded-tr-[2.5rem]">
              <img
                src="/img/todd-line-art.webp"
                alt="Line drawing of Todd Meisler leaning against a brick wall"
                className="w-full"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Why it matters</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              The new first question isn't a search box. It's a conversation.
            </h2>
          </FadeUp>
          <FadeUp delay={0.08}>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-mist md:text-xl">
              "Who's a good plumber near me?" used to go to Google. Now it
              goes to an AI — and the AI names{" "}
              <strong className="font-bold text-slate-900">
                two or three businesses, not ten blue links.
              </strong>{" "}
              If your name isn't in the answer, the customer never hears it.
              AI visibility isn't a magic trick — it sits on top of solid
              SEO. It works hardest for businesses like these:
            </p>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {FITS.map((f, i) => (
              <FadeUp key={f} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`asv-fits-${i}`}
                  className="flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-coal/60 p-6"
                >
                  <BadgeCheck className="mt-1 h-5 w-5 shrink-0 text-flame" />
                  <p className="text-lg leading-relaxed text-slate-700">{f}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.12}>
            <div
              data-testid="asv-both-engines-note"
              className="mt-12 flex flex-col gap-5 rounded-2xl border border-flame/30 bg-coal/60 p-8 md:flex-row md:items-center"
            >
              <div className="glass flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200">
                <Bot className="h-6 w-6 text-flame" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  One engine: AI visibility.
                </p>
                <p className="mt-3 max-w-3xl text-lg leading-relaxed text-mist">
                  When someone asks ChatGPT or Gemini, the AI names a few
                  businesses. Our AI Visibility Program is built to make yours
                  one of them —
                  crawler access, citable pages, structured data, fresh
                  signals, authority. AI Engine optimization only — and it
                  works great alongside our full SEO campaigns.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              A monthly routine, run for you.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mist">
              AI visibility isn't a switch — it's a routine. The same seven
              checkpoints, run on your account every month, with the boring
              parts handled and the results explained.
            </p>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
            {ROUTINE.map((s, i) => (
              <FadeUp key={s.num} delay={(i % 2) * 0.08} className="h-full">
                <div
                  data-testid={`asv-routine-${s.num}`}
                  className="glass h-full rounded-2xl border border-slate-200 p-8 transition-colors duration-300 hover:border-flame/40"
                >
                  <span className="font-display text-4xl font-bold text-flame/40">
                    {s.num}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-lg leading-relaxed text-mist">
                    {s.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <WhatYouGet />

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>The rate</Eyebrow>            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              All AI engines. One flat monthly. Cancel anytime.
            </h2>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
            <FadeUp delay={0.06} className="h-full">
              <div data-testid="asv-pricing-photo" className="h-full overflow-hidden rounded-tr-[2.5rem]">
                <img
                  src="/img/todd-line-art-2.webp"
                  alt="Line drawing of Todd Meisler, founder and strategist at ZD Design"
                  className="h-full w-full object-cover"
                />
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="h-full">
              <div
                data-testid="asv-pricing-card"
                className="flex h-full flex-col rounded-2xl border border-flame bg-coal/70 p-8 shadow-[0_0_40px_rgba(255,85,0,0.12)]"
              >
                <p className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                  AI Visibility Program
                </p>
                <p className="mt-4 font-display text-5xl font-bold text-slate-900">
                  $597<span className="text-2xl text-faint">/mo</span>
                </p>
                <p className="mt-2 text-base text-faint">
                  Full rate — month-to-month, cancel anytime
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                  {[
                    "AI Engine optimization: crawlers, citable pages, structured data, authority building",
                    "Named, quoted and recommended in ChatGPT, Gemini and Claude answers",
                    "Monthly report — example queries we track and where you stand",
                    "No promised Google ranking improvement — most clients see it anyway as a byproduct bonus",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-lg leading-relaxed text-mist">
                      <ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-flame" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <p className="font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                    Main deliverables
                  </p>
                  <ul className="mt-3 space-y-2">
                    {[
                      "Citable pages published for your services and locations",
                      "AI crawler access opened and verified",
                      "Authority building — mentions, reviews and citations across the web",
                      "Monthly report — where you stand, month over month",
                    ].map((d, i) => (
                      <li
                        key={d}
                        data-testid={`asv-deliverable-${i}`}
                        className="flex items-start gap-2 text-base leading-relaxed text-mist"
                      >
                        <Check className="mt-1 h-4 w-4 shrink-0 text-flame" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6">
                  <SampleMonthlyReport
                    theme="dark"
                    testid="asv-sample-report"
                  />
                </div>
                <p className="mt-6 border-t border-slate-200 pt-4 text-base leading-relaxed text-faint">
                  Founding clients lock $397/mo while spots last.
                </p>
                <Link
                  to="/ai-visibility"
                  data-testid="asv-founding-link"
                  className="mt-4 inline-flex items-center gap-2 font-mono text-base font-semibold uppercase tracking-[0.15em] text-flame"
                >
                  See the founding-client offer
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.12}>
            <div
              data-testid="asv-honesty"
              className="mt-10 flex flex-col gap-5 rounded-2xl border border-slate-200 bg-coal/60 p-8 md:flex-row md:items-center"
            >
              <div className="glass flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200">
                <TrendingUp className="h-6 w-6 text-flame" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  No crystal ball. Just the routine.
                </p>
                <p className="mt-3 max-w-3xl text-lg leading-relaxed text-mist">
                  Nobody can guarantee what ChatGPT will say tomorrow — and
                  anyone promising a "#1 AI ranking" is selling the exact
                  thing that burned you last time. What we promise: the
                  routine runs every month, on your accounts, and you see
                  exactly what moved and why.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              Questions about the AI Visibility Program.
            </h2>
          </FadeUp>
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05}>
                <AsvFaqRow
                  item={f}
                  idx={i}
                  open={faq === i}
                  onToggle={() => setFaq(faq === i ? -1 : i)}
                />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <div className="glass rounded-2xl border border-slate-200 p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Ready to be the name AI says?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="asv-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <Link
                to="/ai-answer-checklist"
                data-testid="asv-cta-checklist-link"
                className="mt-5 inline-flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.18em] text-slate-300 underline decoration-flame/60 underline-offset-4 transition-colors hover:text-flame"
              >
                Or get the free AI-answers checklist first
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}